import { useEffect, useRef, useState } from 'react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchRealtimeCounters, type RealtimeCounters } from '@/lib/adapters'
import { ErrorState, Skeleton } from '@/components/States'

const POLL_MS = 5000

/**
 * A2.02 — Real-time monitor. Arzon rolling-hisoblagichlarni (`GET /admin/realtime`)
 * har 5 soniyada poll qiladi: 5-daq faol, xabar/daq+soat, ro'yxat/daq+soat +
 * so'nggi 15 daqiqa xabar-seriyasi (sparkline). Avtomatik yangilanadi (jadval
 * EMAS — o'qish joyi yo'qolmaydi). Oxirgi yangilanish vaqti ko'rsatiladi (SKILL §9).
 */
export function RealtimeMonitor() {
  const t = useT()
  const [data, setData] = useState<RealtimeCounters | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null)
  const [paused, setPaused] = useState(false)
  const firstLoad = useRef(true)

  useEffect(() => {
    if (paused) return
    let alive = true
    const ac = new AbortController()
    const tick = () => {
      fetchRealtimeCounters(ac.signal)
        .then((d) => {
          if (!alive) return
          setData(d)
          setUpdatedAt(new Date())
          setError(null)
          firstLoad.current = false
        })
        .catch((e: unknown) => {
          if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
          if (firstLoad.current) setError(e instanceof Error ? e : new Error(String(e)))
        })
    }
    tick()
    const id = setInterval(tick, POLL_MS)
    return () => {
      alive = false
      ac.abort()
      clearInterval(id)
    }
  }, [paused])

  if (error) return <ErrorState code={error.message} onRetry={() => setError(null)} />

  const maxSeries = data ? Math.max(1, ...data.series.map((p) => p.messages)) : 1
  const loading = !data

  return (
    <>
      <PageHeader
        title={t.rt.title}
        screenId="A2.02"
        description={t.rt.note}
        actions={
          <div className="flex items-center gap-2">
            {updatedAt && (
              <span className="flex items-center gap-1.5 text-xs text-tertiary">
                <span className={`h-1.5 w-1.5 rounded-full ${paused ? 'bg-muted' : 'bg-success animate-pulse'}`} />
                {t.rt.updated} {updatedAt.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={() => setPaused((p) => !p)}
              className="rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-xs text-secondary hover:bg-surface-3"
            >
              {paused ? t.rt.resume : t.rt.pause}
            </button>
          </div>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-5">
          <KPI label={t.rt.active5m} value={data?.active_5m} loading={loading} accent />
          <KPI label={t.rt.msgMin} value={data?.messages_min} loading={loading} sub={data ? `${data.messages_hour}/h` : undefined} />
          <KPI label={t.rt.signupMin} value={data?.signups_min} loading={loading} sub={data ? `${data.signups_hour}/h` : undefined} />
          <KPI label={t.rt.msgHour} value={data?.messages_hour} loading={loading} />
          <KPI label={t.rt.signupHour} value={data?.signups_hour} loading={loading} />
        </div>

        <div className="mt-3 rounded-lg border border-border-default bg-surface-2 p-3">
          <div className="mb-3 text-sm font-medium text-secondary">{t.rt.series}</div>
          {loading ? (
            <Skeleton className="h-32 w-full" />
          ) : !data.series.length ? (
            <div className="py-8 text-center text-sm text-tertiary">{t.dash.noData}</div>
          ) : (
            <div className="flex h-32 items-end gap-1">
              {data.series.map((p, i) => (
                <div key={p.minute + i} className="group relative flex flex-1 flex-col items-center justify-end">
                  <div
                    className="w-full rounded-t bg-accent/70 transition-all group-hover:bg-accent"
                    style={{ height: `${Math.max(3, (p.messages / maxSeries) * 100)}%` }}
                    title={`${p.minute} · ${p.messages}`}
                  />
                  {i % 3 === 0 && <span className="mt-1 text-[10px] text-tertiary">{p.minute}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

function KPI({
  label,
  value,
  loading,
  accent,
  sub,
}: {
  label: string
  value: number | undefined
  loading: boolean
  accent?: boolean
  sub?: string
}) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="text-xs text-tertiary">{label}</div>
      {loading ? (
        <Skeleton className="mt-1.5 h-7 w-14" />
      ) : (
        <div className="mt-0.5 flex items-baseline gap-1.5">
          <span className={`font-mono text-2xl tabular ${accent ? 'text-accent' : 'text-primary'}`}>
            {value?.toLocaleString() ?? '—'}
          </span>
          {sub && <span className="font-mono text-xs text-tertiary">{sub}</span>}
        </div>
      )}
    </div>
  )
}
